import React, { useState, useEffect } from 'react';
import { driverAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DollarSign, TrendingUp, Car, Star, Loader2 } from 'lucide-react';

const DriverDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, earningsRes] = await Promise.all([
        driverAPI.getProfile().catch(() => null),
        driverAPI.getEarnings().catch(() => null),
      ]);
      
      if (profileRes) setProfile(profileRes.data);
      if (earningsRes) setEarnings(earningsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="driver-dashboard">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Driver Dashboard
        </h1>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Earnings</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {formatCurrency(earnings?.total_earnings || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">This Week</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {formatCurrency(earnings?.weekly_earnings || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Rides</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {profile?.total_rides || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                  <Car className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Rating</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {profile?.rating?.toFixed(1) || '5.0'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Info */}
        {profile && (
          <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">Your Vehicle</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                  <Car className="w-10 h-10 text-slate-400" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-slate-900 dark:text-white">
                    {profile.vehicle_year} {profile.vehicle_make} {profile.vehicle_model}
                  </p>
                  <p className="text-slate-500 dark:text-slate-400">
                    {profile.vehicle_color} · {profile.license_plate}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Rides */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">Recent Rides</CardTitle>
          </CardHeader>
          <CardContent>
            {earnings?.recent_rides?.length > 0 ? (
              <div className="space-y-4">
                {earnings.recent_rides.slice(0, 5).map((ride, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {ride.pickup_location?.address || 'Ride'} → {ride.dropoff_location?.address || 'Destination'}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {ride.distance_miles?.toFixed(1)} miles · {ride.duration_minutes} mins
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600 dark:text-green-400">
                        +{formatCurrency(ride.driver_payout + (ride.tip_amount || 0))}
                      </p>
                      {ride.tip_amount > 0 && (
                        <p className="text-xs text-slate-500">incl. {formatCurrency(ride.tip_amount)} tip</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                No rides yet. Go online to start accepting rides!
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverDashboard;
